import { Job, JobState, JobStatus } from './types'
import { log } from '../logger'

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export class JobQueueDatabase {
  private db: IDBDatabase | null = null
  private readonly version = 3

  async init(
    options: { namespace: string } = { namespace: 'web-queue' }
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(
        `queue-${options.namespace}`,
        this.version
      )

      // IndexedDB has no way to cancel an open request, so a blocked
      // open still succeeds later once the other tab goes away. This
      // says we have already given up on it by then.
      let givenUp = false

      request.onerror = () => reject(request.error)

      // Another tab is holding an older version of this database
      // open. Without this the open never settles, and a hub waiting
      // on it stays a leader that never becomes ready — it accepts
      // every queue call and answers none of them.
      request.onblocked = () => {
        givenUp = true
        reject(
          new Error(
            'Job queue database is blocked by another tab holding an older version open'
          )
        )
      }

      request.onsuccess = () => {
        // Nobody is waiting on this open any more. Close it rather
        // than hold a connection no one owns, which would go on to
        // block the next tab that needs to upgrade.
        if (givenUp) {
          request.result.close()
          return
        }

        this.db = request.result
        // Step aside when a newer tab needs to upgrade, so this
        // connection is never the one doing the blocking.
        //
        // This connection is finished once that happens: every later
        // call throws 'Database not initialized' until the page is
        // reloaded on the new version. Log it so that run of errors
        // has a visible cause.
        this.db.onversionchange = () => {
          log.warn(
            'web-queue:database closing, another tab is upgrading the schema'
          )
          this.db?.close()
          this.db = null
        }
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Jobs store
        if (!db.objectStoreNames.contains('jobs')) {
          const jobStore = db.createObjectStore('jobs', {
            keyPath: 'id',
          })
          jobStore.createIndex('status', 'status', {
            unique: false,
          })
          jobStore.createIndex('queueId', 'queueId', {
            unique: false,
          })
          jobStore.createIndex('scheduledAt', 'scheduledAt', {
            unique: false,
          })
          jobStore.createIndex('priority', 'priority', {
            unique: false,
          })
        }
        if (!db.objectStoreNames.contains('stats')) {
          const stateStore = db.createObjectStore('state', {
            keyPath: 'id', // queueId-jobType
          })
          stateStore.createIndex('queueId', 'queueId', {
            unique: false,
          })
          stateStore.createIndex('type', 'type', {
            unique: false,
          })
        }
      }
    })
  }

  async addJob(job: Job): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(
      ['jobs'],
      'readwrite'
    )
    const store = transaction.objectStore('jobs')

    return new Promise((resolve, reject) => {
      const request = store.add(job)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async updateJob(job: Job): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    job.updatedAt = Date.now()
    const transaction = this.db.transaction(
      ['jobs', 'state'],
      'readwrite'
    )
    const jobsStore = transaction.objectStore('jobs')
    const stateStore = transaction.objectStore('state')
    await idbRequest(jobsStore.put(job))
    await idbRequest(
      stateStore.put({
        id: `${job.queueId}-${job.type}`,
        queueId: job.queueId,
        type: job.type,
        lastSuccess: job.updatedAt,
      })
    )
  }

  async getJob(id: string): Promise<Job | undefined> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(['jobs'], 'readonly')
    const store = transaction.objectStore('jobs')

    return new Promise((resolve, reject) => {
      const request = store.get(id)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async getJobsByStatus(
    status: JobStatus,
    type?: string,
    queueId?: string
  ): Promise<Job[]> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(['jobs'], 'readonly')
    const store = transaction.objectStore('jobs')
    const index = store.index('status')

    const jobs = await idbRequest<Job[]>(index.getAll(status))
    if (type || queueId) {
      return jobs.filter((job) => {
        if (type && job.type !== type) return false
        if (queueId && job.queueId !== queueId) return false
        return true
      })
    }
    return jobs
  }

  async getNextJobs(limit: number = 10): Promise<Job[]> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(['jobs'], 'readonly')
    const store = transaction.objectStore('jobs')
    const index = store.index('scheduledAt')
    const jobs: Job[] = []

    return new Promise((resolve, reject) => {
      const request = index.openCursor(null, 'prev')

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result

        if (cursor && jobs.length < limit) {
          const job = cursor.value as Job

          // Get jobs that are pending and scheduled to run
          if (
            job.status === JobStatus.PENDING &&
            job.scheduledAt <= Date.now()
          ) {
            jobs.push(job)
          }

          cursor.continue()
        } else {
          // Sort by priority (higher first) then by scheduledAt (earlier first)
          jobs.sort((a, b) => {
            if (a.priority !== b.priority) {
              return b.priority - a.priority
            }
            return a.scheduledAt - b.scheduledAt
          })

          resolve(jobs)
        }
      }

      request.onerror = () => reject(request.error)
    })
  }

  async deleteJob(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(
      ['jobs'],
      'readwrite'
    )
    const store = transaction.objectStore('jobs')

    return new Promise((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async getJobState(
    queueId: string,
    type: string
  ): Promise<JobState | undefined> {
    if (!this.db) throw new Error('Database not initialized')
    const transaction = this.db.transaction(
      ['state'],
      'readonly'
    )
    const store = transaction.objectStore('state')
    const request = store.get(`${queueId}-${type}`)
    return idbRequest<JobState>(request)
  }

  async getStats(): Promise<{ [status: string]: number }> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(['jobs'], 'readonly')
    const store = transaction.objectStore('jobs')
    const stats: { [status: string]: number } = {}

    return new Promise((resolve, reject) => {
      const request = store.openCursor()

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result

        if (cursor) {
          const job = cursor.value as Job
          stats[job.status] = (stats[job.status] || 0) + 1
          cursor.continue()
        } else {
          resolve(stats)
        }
      }

      request.onerror = () => reject(request.error)
    })
  }

  async cleanup(
    olderThan: number = 24 * 60 * 60_000 // 24 hours
  ): Promise<number> {
    if (!this.db) throw new Error('Database not initialized')

    const cutoff = Date.now() - olderThan
    const transaction = this.db.transaction(
      ['jobs'],
      'readwrite'
    )
    const store = transaction.objectStore('jobs')
    let deletedCount = 0

    return new Promise((resolve, reject) => {
      const request = store.openCursor()

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result

        if (cursor) {
          const job = cursor.value as Job

          // Delete completed or failed jobs older than cutoff
          if (
            (job.status === JobStatus.COMPLETED ||
              job.status === JobStatus.FAILED) &&
            job.updatedAt < cutoff
          ) {
            cursor.delete()
            deletedCount++
          }

          cursor.continue()
        } else {
          resolve(deletedCount)
        }
      }

      request.onerror = () => reject(request.error)
    })
  }

  async clear(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction(
      ['jobs', 'state'],
      'readwrite'
    )
    const jobsStore = transaction.objectStore('jobs')
    const stateStore = transaction.objectStore('state')

    return new Promise((resolve, reject) => {
      let completedOperations = 0
      const totalOperations = 2

      const checkComplete = () => {
        completedOperations++
        if (completedOperations === totalOperations) {
          log.debug('web-queue:database cleared')
          resolve()
        }
      }

      const jobsClearRequest = jobsStore.clear()
      jobsClearRequest.onsuccess = () => checkComplete()
      jobsClearRequest.onerror = () =>
        reject(jobsClearRequest.error)

      const stateClearRequest = stateStore.clear()
      stateClearRequest.onsuccess = () => checkComplete()
      stateClearRequest.onerror = () =>
        reject(stateClearRequest.error)
    })
  }
}
